import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Create,
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  NumberInput,
  TextInput,
  DateInput,
  SelectInput,
  required,
  minValue,
} from "react-admin";
import type { CreateProps } from "react-admin";
import { redirectToProjectModuleDashboard } from "@/containers/projects/redirect-to-project-module-dashboard";
import { useRouteId } from "@/utils/route-utility";

const STATUS = [
  { id: "ACTIVE", name: "Active" },
  { id: "CLOSED", name: "Closed" },
];

export const ProjectKarzanCreate = (props: CreateProps) => {
  const [searchParams] = useSearchParams();
  const routeProjectId = useRouteId();
  const initialProjectId = useMemo(
    () => routeProjectId ?? searchParams.get("projectId") ?? undefined,
    [searchParams, routeProjectId]
  );
  const queryProjectId = searchParams.get("projectId");
  const redirectTo = useMemo(
    () => redirectToProjectModuleDashboard({ routeProjectId, queryProjectId, fallback: "show" }),
    [routeProjectId, queryProjectId]
  );

  return (
    <Create
      {...props}
      redirect={redirectTo}
      transform={(data: Record<string, unknown>) => ({
        projectId: routeProjectId ?? data.projectId,
        lenderName:
          typeof data.lenderName === "string"
            ? data.lenderName.trim() || undefined
            : data.lenderName,
        lenderReference:
          typeof data.lenderReference === "string"
            ? data.lenderReference.trim() || undefined
            : data.lenderReference,
        principalAmount: Number(data.principalAmount),
        disbursementDate: data.disbursementDate || undefined,
        status: data.status,
        remarks: typeof data.remarks === "string" ? data.remarks.trim() || undefined : data.remarks,
      })}
    >
      <SimpleForm defaultValues={{ projectId: initialProjectId, status: "ACTIVE" }}>
        {!routeProjectId && (
          <ReferenceInput source="projectId" reference="projects" perPage={100}>
            <AutocompleteInput optionText="name" label="Project" validate={required()} />
          </ReferenceInput>
        )}
        <TextInput source="lenderName" fullWidth />
        <TextInput source="lenderReference" fullWidth />
        <NumberInput source="principalAmount" validate={[required(), minValue(1)]} />
        <DateInput source="disbursementDate" />
        <SelectInput source="status" choices={STATUS} />
        <TextInput source="remarks" multiline fullWidth />
      </SimpleForm>
    </Create>
  );
};
